import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../services/api';

export default function TemplatesTab() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Global DocuSeal template ID
  const [docusealTemplateId, setDocusealTemplateId] = useState('');
  const [savingGlobal, setSavingGlobal] = useState(false);

  // Per-class template IDs (local edits before save)
  const [classTemplateIds, setClassTemplateIds] = useState({});
  const [savingClass, setSavingClass] = useState({});

  // Per-class annexe button text (local edits before save)
  const [classAnnexeTexts, setClassAnnexeTexts] = useState({});
  const [savingAnnexeText, setSavingAnnexeText] = useState({});

  // Per-class annexe upload state
  const [uploadingAnnexe, setUploadingAnnexe] = useState({});
  const [deletingAnnexe, setDeletingAnnexe] = useState({});
  const fileInputRefs = useRef({});

  // Template names fetched from DocuSeal: { templateId -> name | null | 'loading' | 'error' }
  const [templateNames, setTemplateNames] = useState({});

  const fetchTemplateName = useCallback(async (id) => {
    if (!id || templateNames[id] !== undefined) return;
    setTemplateNames(prev => ({ ...prev, [id]: 'loading' }));
    try {
      const res = await api.get(`/admin/docuseal-template/${id}`);
      setTemplateNames(prev => ({ ...prev, [id]: res.data.name }));
    } catch {
      setTemplateNames(prev => ({ ...prev, [id]: null }));
    }
  }, [templateNames]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get('/classes'),
        api.get('/admin/settings'),
      ]);
      const classList = c.data;
      setClasses(classList);
      setDocusealTemplateId(s.data.docuseal_template_id || '');

      // Init local state for per-class IDs and annexe texts
      const ids = {};
      const texts = {};
      classList.forEach(cl => {
        ids[cl.id] = cl.docusealTemplateId != null ? String(cl.docusealTemplateId) : '';
        texts[cl.id] = cl.annexeBoutonTexte || '';
      });
      setClassTemplateIds(ids);
      setClassAnnexeTexts(texts);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Fetch template names for all class IDs and global ID once loaded
  useEffect(() => {
    const ids = new Set();
    if (docusealTemplateId) ids.add(docusealTemplateId);
    Object.values(classTemplateIds).forEach(id => { if (id) ids.add(id); });
    ids.forEach(id => fetchTemplateName(id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, docusealTemplateId]);

  const saveGlobal = async () => {
    setSavingGlobal(true);
    setError(''); setSuccess('');
    try {
      await api.put('/admin/settings', { docuseal_template_id: docusealTemplateId });
      if (docusealTemplateId) fetchTemplateName(docusealTemplateId);
      setSuccess('Template par défaut sauvegardé.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingGlobal(false);
    }
  };

  const saveClassTemplate = async (classeId) => {
    const rawId = classTemplateIds[classeId];
    const newId = rawId ? parseInt(rawId) : null;
    setSavingClass(prev => ({ ...prev, [classeId]: true }));
    try {
      await api.put(`/classes/${classeId}`, { docusealTemplateId: newId });
      setClasses(prev => prev.map(c => c.id === classeId ? { ...c, docusealTemplateId: newId } : c));
      if (rawId) fetchTemplateName(rawId);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingClass(prev => ({ ...prev, [classeId]: false }));
    }
  };

  const saveAnnexeText = async (classeId) => {
    setSavingAnnexeText(prev => ({ ...prev, [classeId]: true }));
    try {
      await api.put(`/classes/${classeId}`, { annexeBoutonTexte: classAnnexeTexts[classeId] || null });
      setClasses(prev => prev.map(c => c.id === classeId ? { ...c, annexeBoutonTexte: classAnnexeTexts[classeId] || null } : c));
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingAnnexeText(prev => ({ ...prev, [classeId]: false }));
    }
  };

  const uploadAnnexe = async (classeId, file) => {
    if (!file) return;
    setUploadingAnnexe(prev => ({ ...prev, [classeId]: true }));
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/classes/${classeId}/annexe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setClasses(prev => prev.map(c => c.id === classeId ? { ...c, annexePdf: res.data.annexePdf } : c));
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setUploadingAnnexe(prev => ({ ...prev, [classeId]: false }));
      if (fileInputRefs.current[classeId]) fileInputRefs.current[classeId].value = '';
    }
  };

  const deleteAnnexe = async (classeId) => {
    if (!window.confirm('Supprimer le document annexe de cette classe ?')) return;
    setDeletingAnnexe(prev => ({ ...prev, [classeId]: true }));
    try {
      await api.delete(`/classes/${classeId}/annexe`);
      setClasses(prev => prev.map(c => c.id === classeId ? { ...c, annexePdf: null } : c));
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setDeletingAnnexe(prev => ({ ...prev, [classeId]: false }));
    }
  };

  const TemplateName = ({ id }) => {
    if (!id) return null;
    const name = templateNames[id];
    if (name === 'loading') return <span className="text-xs text-gray-400 italic">Chargement...</span>;
    if (name === null) return <span className="text-xs text-red-400">Introuvable</span>;
    if (name) return <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">{name}</span>;
    return null;
  };

  if (loading) return <div className="text-gray-500 text-sm py-8">Chargement...</div>;

  return (
    <div className="space-y-8 max-w-3xl">

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{success}</div>}

      {/* Template DocuSeal par défaut */}
      <div>
        <h3 className="font-medium text-gray-800 mb-3">Template DocuSeal par défaut</h3>
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
            Configurez votre template directement dans l'interface DocuSeal, puis copiez son ID ici.
            Ce template est utilisé pour les classes qui n'ont pas de template propre.
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID du template DocuSeal
              </label>
              <input
                type="number"
                value={docusealTemplateId}
                onChange={e => setDocusealTemplateId(e.target.value)}
                placeholder="ex : 2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {docusealTemplateId && (
                <div className="mt-1">
                  <TemplateName id={docusealTemplateId} />
                </div>
              )}
            </div>
            <button
              onClick={saveGlobal}
              disabled={savingGlobal}
              className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              {savingGlobal ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
            <a
              href={process.env.REACT_APP_DOCUSEAL_URL || 'https://sign.anobel.stagelinks.fr'}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 border border-gray-300 hover:border-gray-400 text-gray-700 text-sm px-4 py-2 rounded-lg transition-colors"
            >
              Ouvrir DocuSeal →
            </a>
          </div>
        </div>
      </div>

      {/* Template par classe */}
      <div>
        <h3 className="font-medium text-gray-800 mb-3">Template par classe</h3>
        <p className="text-xs text-gray-500 mb-3">
          Définissez un ID de template DocuSeal spécifique pour chaque classe. Laissez vide pour utiliser le template par défaut.
        </p>
        {classes.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune classe disponible.</p>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Classe</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Année</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">ID template DocuSeal</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Nom du template</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {classes.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{c.name}</td>
                    <td className="px-4 py-3 text-gray-500">{c.schoolYear?.label}</td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={classTemplateIds[c.id] ?? ''}
                        onChange={e => setClassTemplateIds(prev => ({ ...prev, [c.id]: e.target.value }))}
                        placeholder="ID par défaut"
                        className="w-28 px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <TemplateName id={classTemplateIds[c.id]} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => saveClassTemplate(c.id)}
                        disabled={savingClass[c.id]}
                        className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg transition-colors"
                      >
                        {savingClass[c.id] ? '...' : 'Sauvegarder'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Document annexe par classe */}
      <div>
        <h3 className="font-medium text-gray-800 mb-3">Document annexe par classe</h3>
        <p className="text-xs text-gray-500 mb-3">
          Associez un document PDF annexe à chaque classe. Il sera consultable par les étudiants depuis le formulaire de convention. Vous pouvez personnaliser le texte du bouton.
        </p>
        {classes.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune classe disponible.</p>
        ) : (
          <div className="space-y-3">
            {classes.map(c => (
              <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-medium text-gray-800 text-sm">{c.name}</span>
                  <span className="text-xs text-gray-400">{c.schoolYear?.label}</span>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* PDF upload */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Fichier PDF annexe</label>
                    {c.annexePdf ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-lg flex-1 truncate">
                          annexe.pdf
                        </span>
                        <a
                          href={`/uploads/${c.annexePdf}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 shrink-0"
                        >
                          Voir
                        </a>
                        <button
                          onClick={() => deleteAnnexe(c.id)}
                          disabled={deletingAnnexe[c.id]}
                          className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50 shrink-0"
                        >
                          {deletingAnnexe[c.id] ? '...' : 'Supprimer'}
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer inline-flex items-center gap-2 border border-dashed border-gray-300 hover:border-blue-400 rounded-lg px-3 py-1.5 text-xs text-gray-600 hover:text-blue-600 transition-colors w-full">
                        <span>{uploadingAnnexe[c.id] ? 'Envoi...' : '+ Ajouter un PDF'}</span>
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          disabled={uploadingAnnexe[c.id]}
                          ref={el => { fileInputRefs.current[c.id] = el; }}
                          onChange={e => uploadAnnexe(c.id, e.target.files[0])}
                        />
                      </label>
                    )}
                  </div>

                  {/* Bouton texte */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Texte du bouton</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={classAnnexeTexts[c.id] ?? ''}
                        onChange={e => setClassAnnexeTexts(prev => ({ ...prev, [c.id]: e.target.value }))}
                        placeholder="Consulter annexe"
                        className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                      />
                      <button
                        onClick={() => saveAnnexeText(c.id)}
                        disabled={savingAnnexeText[c.id]}
                        className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg transition-colors shrink-0"
                      >
                        {savingAnnexeText[c.id] ? '...' : 'OK'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
